import { useCallback } from "react";
import { Upload } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { Progress } from "@/components/ui/progress";
import { useMediaUpload } from "@/hooks/useMediaUpload";

interface MediaUploaderProps {
  onUploadComplete?: () => void;
  folderId?: string | null;
}

export const MediaUploader = ({ onUploadComplete, folderId }: MediaUploaderProps) => {
  const { uploadFiles, uploading, progress } = useMediaUpload();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const results = await uploadFiles(acceptedFiles, folderId);
    if (results.length > 0 && onUploadComplete) {
      onUploadComplete();
    }
  }, [uploadFiles, onUploadComplete, folderId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: true,
    disabled: uploading
  });

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary/50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        {isDragActive ? (
          <p className="text-lg">Drop the files here...</p>
        ) : (
          <>
            <p className="text-lg mb-2">Drag & drop images here, or click to select</p>
            <p className="text-sm text-muted-foreground">
              Supports: PNG, JPG, GIF, WebP (Max 10MB)
            </p>
          </>
        )}
      </div>

      {progress.length > 0 && (
        <div className="mt-4 space-y-2">
          {progress.map((item, index) => (
            <div key={index} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="truncate flex-1">{item.fileName}</span>
                <span className="text-muted-foreground ml-2">
                  {item.status === 'uploading' && `${item.progress}%`}
                  {item.status === 'processing' && 'Processing...'}
                  {item.status === 'complete' && 'Complete'}
                  {item.status === 'error' && 'Error'}
                </span>
              </div>
              <Progress value={item.progress} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
